import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  AdditionalCompensationFormFieldsFragment,
  BulkCreateAdditionalCompensationsPageDocument,
  BulkCreateAdditionalCompensationsPageEmployeeFragment,
  BulkCreateContractsAdditionalCompensationDocument,
  BulkCreateContractsAdditionalCompensationMutationVariables,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import AdditionalCompensations from 'modules/contracts/components/BulkAdditionalCompensations'
import Header from 'modules/contracts/components/BulkAdditionalCompensations/Header'
import { mapAmountAndUpperLimitToCents } from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationForm/AdditionalCompensationFormUtils'
import { isRecurrenceValue } from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationWizard/lib/additionalCompensationWizard.shared'
import { manageUpperLimit } from 'modules/contracts/lib/amountStrategyUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

interface AdditionalCompensationLocationState {
  employeeIds: string[]
}

const BulkCreateAdditionalCompensationsPage = () => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const methods = useForm<AdditionalCompensationFormFieldsFragment>()
  const location = useLocation()
  const locationState: AdditionalCompensationLocationState = location.state
  const selectedIds = new Set(locationState?.employeeIds ?? [])
  const [selectedEmployees, setSelectedEmployees] = useState<
    BulkCreateAdditionalCompensationsPageEmployeeFragment[]
  >([])

  const { data, loading, error } = useQuery(BulkCreateAdditionalCompensationsPageDocument, {
    variables: {
      companyId,
      employeeIds: Array.from(selectedIds),
    },
  })

  const [bulkCreateContractsAdditionalCompensation] = useMutation(
    BulkCreateContractsAdditionalCompensationDocument,
    {
      extractorFn: (result) => result.contractsMutations.bulkCreateContractsAdditionalCompensation,
    }
  )

  const payrollConcepts = data?.payroll?.conceptsConnection?.nodes ?? []

  const contracts = useMemo(() => {
    return (
      data?.contracts?.referenceContractsConnection?.nodes.filter(
        (contract) => contract._policies?.edit.can ?? false
      ) ?? []
    )
  }, [data])

  const employees = useMemo(() => {
    return contracts.map((contract) => contract.employee)
  }, [contracts])

  useEffect(() => {
    setSelectedEmployees(employees)
  }, [employees])

  const onRemoveEmployee = (employeeId: string) => {
    setSelectedEmployees((currentEmployees) => {
      if (currentEmployees.length === 1) {
        return currentEmployees
      }

      return currentEmployees.filter((employee) => employee.id !== employeeId)
    })
  }

  const onSubmit = async (values: AdditionalCompensationFormFieldsFragment) => {
    const additionalCompensation = mapAmountAndUpperLimitToCents(values)

    if (
      !additionalCompensation.firstPaymentOn ||
      !isRecurrenceValue(additionalCompensation.recurrence)
    ) {
      return
    }

    const mutationVariables: BulkCreateContractsAdditionalCompensationMutationVariables = {
      companyId,
      employeeIds: selectedEmployees.map((employee) => employee.id),
      payrollConceptId: additionalCompensation.payrollConceptId,
      description: additionalCompensation.description,
      firstPaymentOn: additionalCompensation.firstPaymentOn,
      recurrence: additionalCompensation.recurrence,
      amountStrategyType: additionalCompensation.amountStrategyType,
      amount: additionalCompensation.amountStrategy?.amount,
      upperLimit: manageUpperLimit(additionalCompensation.amountStrategy),
    }

    const result = await bulkCreateContractsAdditionalCompensation({
      variables: mutationVariables,
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('contract_versions.additional_compensation.bulk_create.success'),
        time: 2000,
      })

      navigate(navigation.employees.list.fullPath())
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('contract_versions.additional_compensation.bulk_create.errors.generic'),
        time: 2000,
      })
    }
  }

  return (
    <ResponsivePage.Body loading={loading} error={error}>
      <>
        <Header
          onCreate={methods.handleSubmit(onSubmit)}
          disabledSubmit={!methods.formState.isValid}
        />
        <AdditionalCompensations
          employees={selectedEmployees}
          contracts={contracts}
          payrollConcepts={payrollConcepts}
          methods={methods}
          onRemoveEmployee={onRemoveEmployee}
        />
      </>
    </ResponsivePage.Body>
  )
}

export default BulkCreateAdditionalCompensationsPage
